import { Component, forwardRef, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as dateFns from 'date-fns';

import { Window } from '../../injectable';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarComponent),
      multi: true
    }
  ],
})
export class CalendarComponent implements ControlValueAccessor {

  @Input() placeholder: string;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() muteWeekends = false;
  @Input() showYearDropdown = false;
  @Input() showDeleteBtn = false;

  @Output() clearDOB = new EventEmitter<any>();
  @Output() dateChange = new EventEmitter<any>();


  atTheBottom: boolean;
  current: Date = new Date();
  selected: Date;
  today: Date = new Date();
  tomorrow: Date = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  visible = false;
  weeks: Date[][];
  year: number;

  onChange = (value) => {};
  onTouched = () => {};

  constructor(private element: ElementRef, private window: Window) {
    this.current.setDate(1);
    this.year = this.current.getFullYear();
    this.renderMonth();
  }

  changeYear(year: string): void {
    this.year = parseInt(year, 10);
    this.current.setFullYear(this.year);
    this.renderMonth();
  }

  valueToString() {
    if (!this.selected) {
      return null;
    }
    if (this.isToday(this.selected)) {
      return 'Today';
    }
    if (this.isTomorrow(this.selected)) {
      return 'Tomorrow';
    }
    return new DatePipe('en-AU').transform(this.selected);
  }

  open(event) {
    if (!this.visible) {
      this.atTheBottom = this.element.nativeElement.getBoundingClientRect().bottom / this.window.height() > 1 / 2;
    }
    this.visible = true;
  }

  close(event) {
    this.visible = false;
    this.onTouched();
    event.preventDefault();
    event.stopPropagation();
  }

  renderMonth() {
    let date = new Date(this.current.getTime());

    const Monday = 1;
    const month = date.getMonth();
    const dates = [];

    do {
      dates.push(new Date(date.getTime()));
      date = new Date(date.getTime() + 24 * 60 * 60 * 1000);
    } while (date.getMonth() === month || date.getDay() !== Monday);

    date = new Date(this.current.getTime());
    while (date.getDay() !== Monday) {
      date = new Date(date.getTime() - 24 * 60 * 60 * 1000);
      dates.unshift(new Date(date.getTime()));
    }

    this.weeks = [];
    for (let week, i = 0; i < dates.length; ++i) {
      if (i % 7 === 0) {
        this.weeks.push(week = []);
      }
      week.push(dates[i]);
    }
  }

  changeMonth(delta) {
    const next = new Date(this.current.getTime());
    next.setMonth(this.current.getMonth() + delta);
    this.current = next;
    this.renderMonth();
  }

  select(event, date) {
    if (this.isMuted(date)) {
      return;
    }
    this.writeValue(new Date(date.getTime()));
    this.onChange(this.selected);
    this.dateChange.emit(date);
    this.close(event);
  }

  blur(event) {
    // if (event.relatedTarget !== null) {
    // this.close(event);
    // }
  }

  reset() {
    this.current = new Date((this.selected || this.today).getTime());
    this.current.setDate(1);
    this.renderMonth();
  }

  isToday(date) {
    return this.overlaps(this.today, date);
  }

  isTomorrow(date) {
    return this.overlaps(this.tomorrow, date);
  }

  isSelected(date) {
    return this.selected && this.overlaps(this.selected, date);
  }

  isMuted(date) {
    if (this.muteWeekends && this.dateIsOnWeekend(date)) {
      return true;
    }
    if (this.minDate && date.getTime() < this.minDate.getTime()) {
      return true;
    }
    if (this.maxDate && date.getTime() > this.maxDate.getTime()) {
      return true;
    }
  }

  dateIsOnWeekend(date) {
    const day = date.getDay();
    return [0, 6].indexOf(day) !== -1;
  }

  overlaps(a, b) {
    return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }

  writeValue(value: Date | string) {
    this.selected = typeof value === 'string' ? new Date(value) : value;
    this.reset();
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  @HostListener('document:click', ['$event.target'])
  clickOutside(target?: HTMLElement) {
    if (target && !this.element.nativeElement.contains(target)) {
      this.visible = false;
      this.reset();
    }
  }

  get formatDate(): string {
    return dateFns.format(this.current, 'MMMM YYYY');
  }

  get yearOptions(): number[] {
    const years: number[] = [];
    let currentDay: Date = this.minDate || new Date();
    while (currentDay.getFullYear() <= this.maxYear) {
      years.push(currentDay.getFullYear());
      currentDay = dateFns.addYears(currentDay, 1);
    }
    return years;
  }

  get maxYear(): number {
    return this.maxDate
      ? this.maxDate.getFullYear()
      : new Date().getFullYear();
  }

  clearDateOfBirth(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    this.selected = null;
    this.clearDOB.emit(e);
  }

  clearProp(e: Event) {
    e.stopPropagation();
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/calendar/calendar.component.ts